import { useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };

export const PRESETS = [
  { key: 'today', label: 'Today', range: () => [iso(new Date()), iso(new Date())] },
  { key: '7d', label: 'Last 7 days', range: () => [iso(daysAgo(6)), iso(new Date())] },
  { key: '30d', label: 'Last 30 days', range: () => [iso(daysAgo(29)), iso(new Date())] },
  { key: '90d', label: 'Last 90 days', range: () => [iso(daysAgo(89)), iso(new Date())] },
  { key: 'month', label: 'This month', range: () => { const d = new Date(); return [iso(new Date(d.getFullYear(), d.getMonth(), 1)), iso(d)]; } },
  { key: 'lastmonth', label: 'Last month', range: () => { const d = new Date(); return [iso(new Date(d.getFullYear(), d.getMonth() - 1, 1)), iso(new Date(d.getFullYear(), d.getMonth(), 0))]; } },
  { key: 'ytd', label: 'Year to date', range: () => [iso(new Date(new Date().getFullYear(), 0, 1)), iso(new Date())] },
  { key: 'all', label: 'All time', range: () => ['', ''] },
];

export const rangeFor = (key) => PRESETS.find((p) => p.key === key).range();

// value: { preset, start, end }
export const DateRangeFilter = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState({ start: value.start, end: value.end });
  const label = value.preset === 'custom' ? `${value.start || '…'} → ${value.end || '…'}` : PRESETS.find((p) => p.key === value.preset)?.label;

  const pick = (key) => {
    const [start, end] = rangeFor(key);
    onChange({ preset: key, start, end });
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="inline-flex h-10 items-center gap-2 rounded-full border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-800 shadow-sm transition-colors hover:border-[#14366C]" data-testid="date-range-trigger">
          <CalendarDays className="h-4 w-4 text-[#F58220]" />{label}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-3" data-testid="date-range-popover">
        <div className="grid grid-cols-2 gap-1.5">
          {PRESETS.map((p) => (
            <button key={p.key} onClick={() => pick(p.key)} data-testid={`date-preset-${p.key}`}
              className={`rounded-md px-2.5 py-2 text-left text-xs font-semibold transition-colors ${value.preset === p.key ? 'bg-[#14366C] text-white' : 'bg-slate-50 hover:bg-slate-100'}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="mt-3 border-t pt-3 space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Custom range</p>
          <div className="flex gap-2">
            <input type="date" value={custom.start} max={custom.end || undefined} onChange={(e) => setCustom({ ...custom, start: e.target.value })} className="w-full rounded-md border px-2 py-1.5 text-xs" data-testid="date-custom-start" />
            <input type="date" value={custom.end} min={custom.start || undefined} onChange={(e) => setCustom({ ...custom, end: e.target.value })} className="w-full rounded-md border px-2 py-1.5 text-xs" data-testid="date-custom-end" />
          </div>
          <button disabled={!custom.start || !custom.end} onClick={() => { onChange({ preset: 'custom', ...custom }); setOpen(false); }}
            className="w-full rounded-md bg-[#F58220] py-2 text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50" data-testid="date-custom-apply">
            Apply range
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
